import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Megaphone, Users, Loader2, Send, Clock, User, Truck } from "lucide-react";
import { toast } from "sonner";

export default function AdminBroadcast() {
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [target, setTarget] = useState("all");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    api.get("/admin/broadcasts")
      .then(({ data }) => setHistory(data))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, []);

  const handleSend = async () => {
    if (!message.trim()) { toast.error("Please enter a message"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", { message: message.trim(), title: title.trim() || undefined, target });
      toast.success(`Broadcast sent to ${data.recipients} ${data.target === "agents" ? "agents" : data.target === "customers" ? "customers" : "users"}!`);
      setMessage("");
      setTitle("");
      // Refresh history
      const { data: updated } = await api.get("/admin/broadcasts");
      setHistory(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send broadcast");
    } finally { setSending(false); }
  };

  const targets = [
    { value: "all", label: "Everyone", icon: Users, desc: "All agents & customers" },
    { value: "agents", label: "Agents Only", icon: Truck, desc: "Delivery agents" },
    { value: "customers", label: "Customers Only", icon: User, desc: "Registered customers" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-broadcast-page">
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Notification Center</h1>
        <p className="text-sm text-slate-500 mt-1">Send announcements to agents and customers</p>
      </div>

      {/* Compose Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="broadcast-compose">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-[#FF6B00]/10 rounded-xl flex items-center justify-center">
            <Megaphone className="h-5 w-5 text-[#FF6B00]" />
          </div>
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">New Broadcast</h2>
        </div>

        <div className="space-y-4">
          {/* Target Selection */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">Send to</Label>
            <div className="grid grid-cols-3 gap-3">
              {targets.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTarget(t.value)}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    target === t.value
                      ? "border-[#FF6B00] bg-orange-50 ring-1 ring-[#FF6B00]/20"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`broadcast-target-${t.value}`}
                >
                  <t.icon className={`h-5 w-5 mb-1.5 ${target === t.value ? "text-[#FF6B00]" : "text-slate-400"}`} />
                  <p className="text-sm font-medium text-slate-900">{t.label}</p>
                  <p className="text-[10px] text-slate-400">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700">Title (optional)</Label>
            <Input
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Service Update, Holiday Notice..."
              className="rounded-xl border-slate-200 h-11 mt-1"
              data-testid="broadcast-title"
            />
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700">Message</Label>
            <textarea
              value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your announcement here..."
              rows={4}
              className="w-full mt-1 rounded-xl border border-slate-200 p-3 text-sm focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]/20 focus:outline-none resize-none"
              data-testid="broadcast-message"
            />
          </div>

          <Button onClick={handleSend} disabled={sending || !message.trim()} className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl px-6 h-11 font-semibold shadow-sm shadow-[#FF6B00]/20 btn-press" data-testid="broadcast-send-btn">
            {sending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-5 w-5 mr-2" />}
            {sending ? "Sending..." : "Send Broadcast"}
          </Button>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="broadcast-history">
        <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-4">Recent Broadcasts</h2>
        {loadingHistory ? (
          <Loader2 className="h-6 w-6 animate-spin text-[#FF6B00] mx-auto" />
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No broadcasts sent yet</p>
        ) : (
          <div className="space-y-3">
            {history.slice(0, 10).map((b, i) => (
              <div key={b.created_at || i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                  <Megaphone className="h-4 w-4 text-[#FF6B00]" />
                </div>
                <div className="flex-1 min-w-0">
                  {b.title && <p className="text-sm font-semibold text-slate-900">{b.title}</p>}
                  <p className="text-sm text-slate-600">{b.message}</p>
                  <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {b.created_at ? new Date(b.created_at).toLocaleString() : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
